import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { Accordion as AccordionPrimitive } from 'radix-ui'
import type * as React from 'react'
import { useCallback, useEffect, useState } from 'react'

import { transitions } from '@/lib/motion/presets'
import { cn } from '@/lib/utils'

function Accordion({
  className,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Root>) {
  return (
    <AccordionPrimitive.Root
      data-slot="accordion"
      className={cn('flex w-full flex-col', className)}
      {...props}
    />
  )
}

function AccordionItem({
  className,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={cn('not-last:border-b', className)}
      {...props}
    />
  )
}

function AccordionTrigger({
  className,
  children,
  leading,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger> & {
  leading?: React.ReactNode
}) {
  return (
    <AccordionPrimitive.Header
      className={cn('flex', leading && 'items-center gap-2')}
    >
      {leading}
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          'group/accordion-trigger relative flex flex-1 cursor-pointer items-start justify-between rounded-lg border border-transparent py-2.5 text-left text-sm font-medium transition-all outline-none hover:underline focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:after:border-ring disabled:pointer-events-none disabled:opacity-50 **:data-[slot=accordion-trigger-icon]:ml-auto **:data-[slot=accordion-trigger-icon]:size-4 **:data-[slot=accordion-trigger-icon]:text-muted-foreground',
          className
        )}
        {...props}
      >
        {children}
        <ChevronDownIcon
          data-slot="accordion-trigger-icon"
          className="pointer-events-none shrink-0 group-aria-expanded/accordion-trigger:hidden"
        />
        <ChevronUpIcon
          data-slot="accordion-trigger-icon"
          className="pointer-events-none hidden shrink-0 group-aria-expanded/accordion-trigger:inline"
        />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  )
}

function AccordionContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
  const [node, setNode] = useState<HTMLDivElement | null>(null)
  const [isOpen, setIsOpen] = useState(false)

  // Radix's Collapsible (which Accordion's Content wraps) stamps
  // `data-state` on this element and flips it in place rather than
  // remounting, so a plain read on mount would miss later toggles. Mirror it
  // into React state via a MutationObserver so Motion's own
  // initial/animate/exit can drive the height animation instead of the
  // `animate-accordion-*` CSS keyframes (which read the
  // `--radix-accordion-content-height` var hack).
  useEffect(() => {
    if (!node) return

    setIsOpen(node.dataset.state === 'open')

    const observer = new MutationObserver(() => {
      setIsOpen(node.dataset.state === 'open')
    })

    observer.observe(node, {
      attributes: true,
      attributeFilter: ['data-state'],
    })

    return () => observer.disconnect()
  }, [node])

  const setRefs = useCallback((element: HTMLDivElement | null) => {
    setNode(element)
  }, [])

  return (
    <AccordionPrimitive.Content
      ref={setRefs}
      data-slot="accordion-content"
      forceMount
      className="overflow-hidden text-sm"
      {...props}
    >
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="accordion-content"
            initial={{ height: 0, opacity: 0, y: -4 }}
            animate={{
              height: 'auto',
              opacity: 1,
              y: 0,
              transition: transitions.expand,
            }}
            exit={{
              height: 0,
              opacity: 0,
              y: -4,
              transition: transitions.collapse,
            }}
            className="overflow-hidden"
          >
            <div
              className={cn(
                'pt-0 pb-2.5 [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground [&_p:not(:last-child)]:mb-4',
                className
              )}
            >
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </AccordionPrimitive.Content>
  )
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
